'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  FiFileText, 
  FiTerminal,
  FiCode,
  FiCopy,
  FiZap,
  FiSend,
  FiInfo,
  FiCheckCircle,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiChevronRight,
  FiX
} from 'react-icons/fi'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import Swal from 'sweetalert2'
import { INVOICE_PRESETS, INVOICE_DOCUMENTATION, GRAVADA_VARIANTS } from '@/lib/facturacion-constants'

export default function ComprobantesPage() {
  const [activeTab, setActiveTab] = useState('gravada')
  const [token, setToken] = useState<string>('')
  const [showToken, setShowToken] = useState(false)
  const [loading, setLoading] = useState(false)
  const [requestBody, setRequestBody] = useState('')
  const [response, setResponse] = useState<any>(null)
  const [responseTime, setResponseTime] = useState<string | null>(null)
  const [selectedGravadaVariant, setSelectedGravadaVariant] = useState('general')
  const [showCalculationNote, setShowCalculationNote] = useState(true)

  useEffect(() => {
    fetchToken()
  }, [])

  useEffect(() => {
    if (activeTab === 'gravada') {
      setRequestBody(JSON.stringify(GRAVADA_VARIANTS[selectedGravadaVariant], null, 4))
      if (selectedGravadaVariant === 'restaurante') setShowCalculationNote(true)
    } else if (INVOICE_PRESETS[activeTab]) {
      setRequestBody(JSON.stringify(INVOICE_PRESETS[activeTab], null, 4))
    }
  }, [activeTab, selectedGravadaVariant])

  const fetchToken = async () => {
    try {
      const res = await fetch('/api/user/token')
      const data = await res.json()
      if (data.success) setToken(data.token)
    } catch (e) {
      console.error(e)
    }
  }

  const handleTest = async () => {
    if (!token) return Swal.fire('Error', 'Ingresa un token de acceso', 'error')
    
    // Validar que la claveSecreta sea válida
    try {
      const parsedBody = JSON.parse(requestBody)
      const claveSecreta = parsedBody.claveSecreta
      
      if (!claveSecreta || claveSecreta.trim() === '') {
        return Swal.fire({
          icon: 'error',
          title: 'Clave Secreta Requerida',
          html: 'Debes incluir el campo <strong>claveSecreta</strong> en el JSON para identificar la empresa.',
          confirmButtonColor: '#3b82f6'
        })
      }
      
      if (claveSecreta === 'tu-clave-secreta-aqui' || claveSecreta === 'TU_CLAVE') {
        return Swal.fire({
          icon: 'warning',
          title: 'Clave Secreta Inválida',
          html: 'Reemplaza <code>tu-clave-secreta-aqui</code> con la <strong>clave secreta real</strong> de tu empresa.<br><br>Puedes encontrarla en <strong>Gestión de Empresas → Configurar</strong>.',
          confirmButtonColor: '#3b82f6'
        })
      }
    } catch (e) {
      return Swal.fire({
        icon: 'error',
        title: 'JSON Inválido',
        text: 'El JSON del payload tiene errores de sintaxis. Revísalo antes de continuar.',
        confirmButtonColor: '#3b82f6'
      })
    }
    
    setLoading(true)
    setResponse(null)
    setResponseTime(null)

    const startTime = performance.now()
    const endpoint = 'https://miapi.cloud/apifact/invoice/create'

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token.trim()}`,
          'Content-Type': 'application/json'
        },
        body: requestBody
      })
      const data = await res.json()
      const endTime = performance.now()
      
      const timeSecs = (endTime - startTime) / 1000
      setResponseTime(timeSecs < 1 ? `${(timeSecs * 1000).toFixed(0)}ms` : `${timeSecs.toFixed(2)}s`)
      setResponse(data)

      if (res.ok) {
        fetch('/api/user/record', { method: 'POST' }).catch(err => console.error(err))
      }
    } catch (e: any) {
      setResponse({ error: 'Error al conectar con la API', details: e.message })
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string, label: string = 'Copiado') => {
    navigator.clipboard.writeText(text)
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: `${label} al portapapeles`,
      showConfirmButton: false,
      timer: 1500
    })
  }

  const getFlattenedDocs = () => {
    try {
      const json = JSON.parse(requestBody)
      const result: { key: string; desc: string; type: string }[] = []
      
      const process = (obj: any, path = '') => {
        if (!obj || typeof obj !== 'object') return
        
        Object.keys(obj).forEach(key => {
          const fullPath = path ? `${path}.${key}` : key
          const value = obj[key]
          
          let typeStr: string = typeof value
          if (Array.isArray(value)) typeStr = 'Array'
          else if (value === null) typeStr = 'null'

          if (INVOICE_DOCUMENTATION[key]) {
            result.push({ key: fullPath, desc: INVOICE_DOCUMENTATION[key], type: typeStr })
          }

          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            process(value, fullPath)
          } else if (Array.isArray(value) && value.length > 0) {
            process(value[0], `${fullPath}[0]`)
          }
        })
      }
      
      process(json)
      return result
    } catch (e) {
      return []
    }
  }

  const detailedDocs = getFlattenedDocs()

  return (
    <div className="max-w-[1700px] mx-auto space-y-8 pb-20 animate-in fade-in duration-700 font-sans">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-50">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-[11px] font-bold uppercase tracking-wider">
            <FiZap className="animate-pulse" /> SUNAT UBL 2.1 Full Support
          </div>
          <h1 className="text-4xl font-[900] text-gray-900 tracking-tight leading-none">Comprobantes Electrónicos</h1>
          <p className="text-gray-500 max-w-xl text-md font-medium">
            Prueba las 16 casuísticas oficiales de SUNAT. Edita, valida y emite documentos en tiempo real.
          </p>
        </div>
        
        <div className="flex flex-col gap-3">
          <div className="relative group">
            <div className="absolute -top-2.5 left-4 px-2 bg-white text-[10px] font-[800] text-gray-400 uppercase tracking-[0.2em]">
              Access Token
            </div>
            <div className="flex items-center bg-gray-50/50 border-2 border-gray-100 rounded-2xl overflow-hidden focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-50 transition-all shadow-sm">
              <input 
                type={showToken ? 'text' : 'password'}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Bearer token..."
                className="w-full sm:w-80 px-5 py-4 bg-transparent text-sm font-mono outline-none"
              />
              <div className="flex items-center px-3 border-l border-gray-200 bg-white/30">
                <button onClick={() => setShowToken(!showToken)} className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                  {showToken ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
                <button onClick={() => copyToClipboard(token, 'Token')} className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                  <FiCopy size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="gravada" onValueChange={setActiveTab} className="space-y-8">
        {/* Selector de Casuísticas con Scrollbar Visible */}
        <div className="bg-white p-4 rounded-[2rem] shadow-sm border border-gray-50 relative">
          <div className="overflow-x-auto pb-4 custom-scrollbar">
            <TabsList className="bg-transparent h-auto p-0 flex flex-nowrap w-max gap-2 pr-4">
              {Object.keys(INVOICE_PRESETS).map((key) => (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className="rounded-xl px-6 py-3 text-[11px] font-[800] uppercase tracking-wider text-gray-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all active:scale-95 whitespace-nowrap border border-transparent data-[state=active]:border-blue-700"
                >
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className="absolute right-6 top-1/2 -translate-y-1/2 bg-gradient-to-l from-white via-white/80 to-transparent w-20 h-full pointer-events-none flex items-center justify-end pr-2 opacity-60">
             <FiChevronRight className="text-gray-300" />
          </div>
        </div>

        <div className="grid grid-cols-1 space-y-8">
          {/* Fila Superior: Payload y Guía (Dos Columnas del mismo tamaño) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Columna 1: Editor Payload */}
            <Card className="border-none shadow-2xl rounded-[2.5rem] overflow-hidden bg-[#0f172a] flex flex-col h-[700px]">
              <CardHeader className="border-b border-white/5 py-5 px-8 flex flex-row items-center justify-between bg-[#1e293b]/30 flex-none gap-4">
                <div className="flex items-center gap-3">
                  <FiTerminal className="text-blue-400 text-xl" />
                  <CardTitle className="text-base font-extrabold text-white">Payload Solicitud</CardTitle>
                </div>
                
                {activeTab === 'gravada' && (
                  <div className="flex-1 max-w-[280px]">
                    <select 
                      value={selectedGravadaVariant}
                      onChange={(e) => setSelectedGravadaVariant(e.target.value)}
                      className="w-full bg-[#0f172a] text-blue-300 border border-white/10 rounded-xl px-4 py-2 text-xs font-bold focus:outline-none focus:border-blue-500 transition-all cursor-pointer hover:bg-white/5 appearance-none"
                    >
                      <option value="general" className="text-gray-900 bg-white">Gravada General (IGV 18%)</option>
                      <option value="restaurante" className="text-gray-900 bg-white">Gravada Restaurante (IGV 10.5%)</option>
                      <option value="descuento_obs" className="text-gray-900 bg-white">Gravada Restaurante (8% + 2%)</option>
                    </select>
                  </div>
                )}

                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(requestBody)} className="text-gray-400 hover:text-white rounded-xl">
                   <FiCopy />
                </Button>
              </CardHeader>
              <CardContent className="p-0 relative flex-grow overflow-hidden">
                <textarea 
                  value={requestBody}
                  onChange={(e) => setRequestBody(e.target.value)}
                  spellCheck={false}
                  className="w-full h-full p-8 font-mono text-[13px] bg-transparent text-blue-300 focus:outline-none resize-none leading-relaxed custom-scrollbar-dark"
                />

                {selectedGravadaVariant === 'restaurante' && showCalculationNote && (
                  <div className="absolute top-24 left-8 right-8 bg-blue-900/60 border border-blue-500/40 p-5 rounded-2xl backdrop-blur-xl animate-in slide-in-from-top-2 duration-300 z-10 shadow-2xl overflow-hidden group/note">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex gap-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center shrink-0 border border-blue-500/20">
                          <FiInfo className="text-blue-400" size={20} />
                        </div>
                        <div className="space-y-1.5">
                          <p className="text-blue-100 text-[13px] font-black tracking-tight">Nota sobre los cálculos:</p>
                          <p className="text-blue-200/90 text-[12px] leading-relaxed font-medium">
                            Para una base de S/ 12.00 a una tasa de 10.5%, el IGV es S/ 1.26 y el precio unitario es S/ 13.26.<br/>
                            Si usas el array detallado de impuestos (8% IGV + 2.5% IPM), el sistema también lo sumará correctamente a 10.5% en el PDF.
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-1">
                        <button 
                          onClick={() => copyToClipboard(`Para una base de S/ 12.00 a una tasa de 10.5%, el IGV es S/ 1.26 y el precio unitario es S/ 13.26. Si usas el array detallado de impuestos (8% IGV + 2.5% IPM), el sistema también lo sumará correctamente a 10.5% en el PDF.`, "Nota")}
                          className="p-2 text-blue-400/50 hover:text-blue-200 hover:bg-white/10 rounded-lg transition-all"
                          title="Copiar nota"
                        >
                          <FiCopy size={16} />
                        </button>
                        <button 
                          onClick={() => setShowCalculationNote(false)}
                          className="p-2 text-blue-400/50 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all"
                          title="Cerrar"
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="absolute bottom-8 right-8">
                   <Button 
                    onClick={handleTest}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-500 text-white px-10 py-7 rounded-2xl font-black text-sm shadow-xl gap-3 transition-all active:scale-95 border-b-4 border-blue-800"
                  >
                    {loading ? (
                      <div className="h-5 w-5 border-3 border-white/20 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <FiSend /> EMITIR COMPROBANTE
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Columna 2: Guía Técnica UBL 2.1 */}
            <Card className="border-none shadow-xl rounded-[2.5rem] bg-white overflow-hidden flex flex-col h-[700px] border border-gray-50">
              <CardHeader className="bg-gray-50/50 border-b py-5 px-8 flex-none flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <FiInfo className="text-blue-600 text-xl" />
                  <CardTitle className="text-base font-extrabold text-gray-800">Guía Técnica de Campos</CardTitle>
                </div>
                
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-xl border-blue-200 text-blue-600 hover:bg-blue-50 font-bold gap-2">
                      <FiEye /> Detalle Técnico
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col rounded-[2rem] border-none shadow-2xl">
                    <DialogHeader className="p-8 bg-blue-600 text-white flex-none">
                      <DialogTitle className="text-2xl font-black flex items-center gap-3">
                        <FiCode /> Diccionario Técnico de Campos
                      </DialogTitle>
                      <p className="text-blue-100 text-sm mt-1">Detalle exhaustivo de cada línea del JSON de facturación.</p>
                    </DialogHeader>
                    <div className="flex-grow overflow-y-auto p-8 custom-scrollbar bg-gray-50">
                      <div className="grid grid-cols-1 gap-4">
                        {detailedDocs.map((doc, i) => (
                          <div key={i} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-2">
                              <code className="text-blue-600 font-black text-sm">{doc.key}</code>
                              <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                {doc.type}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed">{doc.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-0 flex flex-col flex-grow overflow-hidden">
                <div className="flex-grow overflow-y-auto px-8 py-6 space-y-4 custom-scrollbar">
                  {Object.entries(INVOICE_DOCUMENTATION).map(([key, desc]) => (
                    <div key={key} className="p-5 rounded-2xl border border-gray-50 bg-gray-50/30 hover:bg-blue-50/30 hover:border-blue-100 transition-all group">
                      <div className="flex items-center justify-between mb-1">
                        <code className="text-blue-600 font-black text-xs">{key}</code>
                        <span className="text-[10px] text-gray-300 font-bold group-hover:text-blue-400">UBL Field</span>
                      </div>
                      <p className="text-[13px] text-gray-500 font-medium leading-relaxed">{desc}</p>
                    </div>
                  ))}
                </div>
                <div className="p-8 bg-slate-900 text-white flex-none flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-black uppercase text-blue-400 mb-1 tracking-widest">Environment URL</p>
                    <code className="text-[11px] opacity-70 font-mono">https://miapi.cloud/apifact/invoice/create</code>
                  </div>
                  <FiCheckCircle className="text-green-500 text-2xl" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* TIP Informativo Permanente */}
          <div className="w-full bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl px-6 py-4 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xl">💡</span>
              </div>
              <p className="text-emerald-800 text-sm font-medium leading-relaxed">
                <strong>TIP:</strong> El sistema recalcula automáticamente los totales (mtoOperGravadas, mtoIGV, total) y genera el totalTexto si no los envías. Esto asegura consistencia con SUNAT y simplifica tu integración.
              </p>
            </div>
          </div>

          {/* Fila Inferior: Respuesta API (Horizontal de ancho completo) */}
          <Card className={`w-full border-none transition-all duration-700 rounded-[2.5rem] overflow-hidden ${response ? 'shadow-2xl shadow-blue-100 border-2 border-blue-100' : 'shadow-xl bg-white border border-gray-100'}`}>
             <CardHeader className="py-6 px-10 border-b border-gray-50 flex flex-row items-center justify-between bg-white flex-none">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${response?.success ? 'bg-green-100 text-green-600 border border-green-200' : 'bg-gray-100 text-gray-400 border border-gray-200'}`}>
                    <FiCode className="text-xl" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-extrabold text-gray-800 tracking-tight">Respuesta del Servidor</CardTitle>
                    <div className="flex items-center gap-3 mt-1">
                       {responseTime && <span className="text-[10px] font-black px-4 py-1.5 bg-gray-50 text-gray-500 rounded-full border border-gray-100">{responseTime}</span>}
                       {response && (
                         <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${(response.success || response.respuesta?.success) ? 'text-green-500' : 'text-red-500'}`}>
                           {(response.success || response.respuesta?.success) ? 'OPERACIÓN EXITOSA' : 'ERROR EN PROCESO'}
                         </span>
                       )}
                    </div>
                  </div>
                </div>
                {response && (
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(JSON.stringify(response, null, 2))} className="rounded-xl border-gray-200 hover:border-blue-600 hover:text-blue-600 h-11 px-6 font-bold gap-2">
                    <FiCopy /> Copiar Respuesta
                  </Button>
                )}
             </CardHeader>
             <CardContent className="p-10 bg-white">
                {response ? (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-8 bg-[#0f172a] p-8 rounded-[2.5rem] shadow-inner relative group">
                       <pre className="text-green-400 text-[13px] font-mono overflow-auto max-h-[350px] custom-scrollbar-dark leading-relaxed">
                          {JSON.stringify(response, null, 2)}
                       </pre>
                    </div>
                    <div className="lg:col-span-4 space-y-6 self-center">
                       {(response.success || response.respuesta?.success) ? (
                         <>
                            <div className="p-8 rounded-[2rem] bg-blue-50 border border-blue-100 text-center space-y-4">
                               <div className="w-16 h-16 bg-blue-600 text-white rounded-3xl flex items-center justify-center mx-auto shadow-xl shadow-blue-200">
                                  <FiCheckCircle size={32} />
                               </div>
                               <h4 className="font-black text-blue-900 text-xl">¡Comprobante Emitido!</h4>
                               <p className="text-sm text-blue-700 font-medium">{(response.mensaje || response.respuesta?.mensaje) || 'Documento procesado correctamente por SUNAT.'}</p>
                            </div>
                            <div className="grid grid-cols-1 gap-4">
                              {(response.pdf_a4 || response.respuesta?.['pdf-a4']) && (
                                <a href={response.pdf_a4 || response.respuesta?.['pdf-a4']} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 py-5 bg-white border-2 border-gray-100 hover:border-blue-600 hover:text-blue-600 rounded-2xl text-[13px] font-black transition-all shadow-sm group">
                                  <FiFileText className="text-lg group-hover:scale-110 transition-transform" /> VER PDF (FORMATO A4)
                                </a>
                              )}
                              {(response.xml_firmado || response.respuesta?.['xml-firmado']) && (
                                <a href={response.xml_firmado || response.respuesta?.['xml-firmado']} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 py-5 bg-white border-2 border-gray-100 hover:border-blue-600 hover:text-blue-600 rounded-2xl text-[13px] font-black transition-all shadow-sm group">
                                  <FiDownload className="text-lg group-hover:translate-y-0.5 transition-transform" /> DESCARGAR XML FIRMADO
                                </a>
                              )}
                            </div>
                         </>
                       ) : (
                         <div className="p-10 rounded-[2.5rem] bg-red-50 border border-red-100 text-center space-y-4">
                           <div className="w-16 h-16 bg-red-500 text-white rounded-3xl flex items-center justify-center mx-auto">
                              <FiInfo size={32} />
                           </div>
                           <h4 className="font-black text-red-900 text-xl">Error de Validación</h4>
                           <p className="text-sm text-red-700 font-medium">Revisa el payload y los campos obligatorios indicados en la guía técnica.</p>
                         </div>
                       )}
                    </div>
                  </div>
                ) : (
                  <div className="py-24 flex flex-col items-center justify-center text-gray-300 gap-6 border-2 border-dashed border-gray-100 rounded-[3rem]">
                    <div className="w-24 h-24 rounded-[2.5rem] bg-gray-50 flex items-center justify-center shadow-inner">
                      <FiCode size={40} className="opacity-10" />
                    </div>
                    <div className="text-center space-y-2">
                       <p className="text-[12px] font-black uppercase tracking-[0.4em] opacity-40">Modo Standby</p>
                       <p className="text-sm font-medium text-gray-400">Haz clic en "EMITIR COMPROBANTE" para ver la respuesta aquí</p>
                    </div>
                  </div>
                )}
             </CardContent>
          </Card>
        </div>
      </Tabs>

      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        
        .custom-scrollbar-dark::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar-dark::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar-dark::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; }
      `}</style>
    </div>
  )
}
